/**
 * @file ui/src/pages/Home.tsx
 * @description Public bilingual homepage for Spark KNE.
 * @copyright 2026 Seth Zhai. BSL 1.1
 */

import { useTranslation } from 'react-i18next'
import { setLocale as persistLocale } from '../i18n'
import styles from './Home.module.css'

type Locale = 'en' | 'zh'

const CONTACT_URL = 'mailto:seth@knespark.com'
const EDGE_STORE_URL = 'https://microsoftedge.microsoft.com/addons/detail/egcpfobobpfkikgogbmejiogjglcjlhc'
const COZE_STORE_URL = 'https://www.coze.cn/store/plugin/7672601222863519782?from=plugin_card'

function installUrl(locale: Locale, placement: 'hero' | 'workflow') {
  const params = new URLSearchParams({
    locale,
    placement,
    utm_source: 'knespark.com',
    utm_medium: 'homepage',
    utm_campaign: 'spark_public_beta',
  })
  return `/api/v1/public/install?${params.toString()}`
}

const copy = {
  en: {
    nav: ['Product', 'Workflow', 'Company', 'Team'],
    navLabel: 'Main navigation',
    testInvite: 'Install for Chrome',
    edgeInvite: 'Install for Edge',
    viewProduct: 'View product',
    betaNote: 'Spark 0.3.1 is being prepared across Chrome, Edge, and the China Coze plugin store. Store-visible versions update only after each platform review. Browser verification works up to 10 times per 5 hours without signing in.',
    windowLabel: 'verification panel',
    demoBrand: 'SPARK',
    demoVerify: 'VERIFY',
    heroKicker: 'Independent knowledge trust layer for AI',
    heroTitle: 'Spark verifies selected AI and web knowledge claims across your daily tools.',
    heroLede: 'Select an answer in ChatGPT, Qwen, Claude, or any webpage. Spark checks whether the knowledge claim has applicable source support, then shows the trust state, sources, and evidence gaps.',
    productKicker: 'Product',
    productTitle: 'A verification plugin, not another chatbot.',
    productBody: 'AI models are becoming faster and more capable, but users still need to know whether an answer is grounded in real-world knowledge or produced as a confident hallucination. Spark adds a separate KNE knowledge trust layer beside the model, so answers can be treated as claims to inspect rather than truth to accept.',
    accessKicker: 'Available clients',
    accessTitle: 'One KNE assessment layer, available through three focused entry points.',
    accessBody: 'Chrome and Edge use the same selected-text verification flow. The Coze integration brings the same public Spark API result into an agent workflow without exposing the private KNE Core.',
    accessStatusLabel: 'Status',
    accessItems: [
      ['Chrome', 'Public', 'Spark 0.3.1 adds bilingual claim query planning while keeping the selected-text side-panel flow and the KNE public result contract.', 'Install for Chrome', 'chrome'],
      ['Microsoft Edge', 'Public', 'Spark 0.3.1 uses the same bilingual query plan, verification flow, and KNE public result contract as Chrome.', 'Install for Edge', 'edge'],
      ['Coze', 'Public', 'Spark 0.3.1 prepares the same bilingual evidence-retrieval input for agent workflows without exposing the private KNE Core.', 'Open in Coze', 'coze'],
    ],
    capabilities: [
      ['Claim detection', 'Spark helps identify whether an AI answer contains a factual, safety, medical, legal, technical, or time-sensitive knowledge claim.'],
      ['Evidence-based trust state', 'Spark uses KNE source candidates, evidence rules, applicability gates, and trust policy rules to return states such as source supported, source missing, needs review, conflicted, outdated, high risk, or not knowledge.'],
      ['Contribution queue', 'Users can submit sources, corrections, and challenges. These contributions enter review instead of directly changing the KNE core data.'],
    ],
    outcomesKicker: 'Why it matters',
    outcomesTitle: 'As AI becomes the interface to knowledge, trust needs its own infrastructure.',
    outcomesBody: 'Agents, skills, and tools give AI hands and feet. Spark focuses on the knowledge layer beneath those actions: what should be trusted, what needs a source, and what must be reviewed before it affects human decisions.',
    outcomes: [
      ['Reduce hallucination risk', 'Turn AI answers into reviewable claims with source and risk signals.'],
      ['Protect high-risk decisions', 'Flag safety, health, legal, and other sensitive claims before users rely on them.'],
      ['Build KNE over time', 'Use reviewed human corrections and source submissions to grow a claim-evidence knowledge trust graph.'],
      ['Stay model independent', 'Spark is designed to work beside mainstream AI platforms instead of replacing them.'],
    ],
    demoKicker: 'Workflow',
    demoTitle: 'A lightweight verification flow inside daily AI use.',
    demoBody: 'The first product stage focuses on active selected-text verification. Users choose what to verify; Spark does not automatically scrape pages by default. Results are cautious trust states, not final truth certification.',
    openLive: 'Open Chrome Web Store',
    demoPanelKicker: 'AI output',
    demoPanelTitle: 'Spark treats model output as claims.',
    entries: [
      ['Claim', 'Wild mushrooms cannot be judged safe by color alone.', 'Detected safety-related factual claim'],
      ['Risk', 'Food toxicity and poisoning advice requires cautious source handling.', 'High-risk safety domain'],
      ['Source hint', 'CDC, Poison Control, or official medical/public health sources are preferred.', 'KNE source policy'],
    ],
    reviewable: 'reviewable',
    basis: 'Basis',
    actions: ['Verify', 'Challenge', 'Submit source'],
    pathKicker: 'KNE source layer',
    pathTitle: 'Verification is separated from generation.',
    path: 'AI answer -> Claim -> Source evidence -> Trust state -> Review queue',
    claims: [
      ['source_supported', 'A claim has relevant, acceptable source support for its risk level.'],
      ['source_missing', 'No candidate source in the current KNE reference corpus supports the claim.'],
      ['needs_review', 'High-risk, conflicted, outdated, or insufficiently sourced claims require human review.'],
    ],
    demoSample: 'trust state',
    claimSource: 'Output: cautious verification result, not final truth certification',
    cases: [
      ['Everyday AI users', 'Check whether AI answers about food, safety, health, finance, and technology are supported by reliable sources.', ['Verify answers', 'Evidence gaps', 'Risk warning']],
      ['Researchers and builders', 'Separate model output from source evidence while collecting corrections and source submissions.', ['Source layer', 'Review queue', 'Claim history']],
      ['Future KNE operators', 'Maintain authoritative source registries, trust policy versions, and reviewed contribution pipelines.', ['Source registry', 'Trust policy', 'Human review']],
    ],
    businessKicker: 'Digital-native business model',
    businessTitle: 'Delivered through cloud software, browser extensions, and approved integrations.',
    business: [
      'Chrome and Edge extensions plus approved platform integrations consume one public Spark verification API.',
      'Early testing focuses on users who already rely on AI tools. Spark records lightweight source context across ChatGPT, Qwen, Claude, Gemini, Doubao, and ordinary web pages after user-initiated verification.',
      'Planned revenue model: individual subscriptions, team verification seats, and API access for KNE verification services.',
    ],
    companyKicker: 'Company',
    companyTitle: 'KNE Spark is building an independent knowledge trust layer for the AI era.',
    companyBody: [
      'The current Spark 2.0 focus is narrow and testable: help users verify knowledge claims while using mainstream AI products. The old Spark Web Chat has been retired as the main product entry.',
      'The long-term KNE vision is a claim-evidence knowledge trust system that protects real-world human knowledge from hallucination, pollution, weak sourcing, and commercial manipulation. Spark is the first user-facing wedge into that system.',
    ],
    teamKicker: 'Team',
    teamTitle: 'Founder-led product and engineering.',
    founder: 'Founder and builder of Spark. Product, engineering, AI memory, KNE source systems, and knowledge verification.',
    privacy: 'Privacy Policy',
    support: 'Support',
    terms: 'Terms of Use',
    panel: {
      title: 'Spark Verify',
      user1: 'Can I identify poisonous mushrooms by bright color?',
      spark: 'Spark detects a safety-risk claim and checks whether source support exists.',
      user2: 'Trust state: high risk. Use official toxicology or public health sources.',
      cards: [
        ['claim', 'The answer contains knowledge that may affect real-world safety.'],
        ['source', 'Official medical or public health sources are required.'],
        ['review', 'High-risk claims should not be treated as automatically certified.'],
      ],
    },
  },
  zh: {
    nav: ['产品', '流程', '公司', '团队'],
    navLabel: '主导航',
    testInvite: '安装 Chrome 插件',
    edgeInvite: '安装 Edge 插件',
    viewProduct: '查看产品',
    betaNote: 'Spark 0.3.1 正在为 Chrome、Edge 和扣子国内版准备更新；只有各平台审核通过后，商店显示版本才会变化。浏览器端无需登录即可每 5 小时验证 10 次。',
    windowLabel: '验证面板',
    demoBrand: 'SPARK',
    demoVerify: '验证',
    heroKicker: '独立于大模型的知识可信层',
    heroTitle: 'Spark 验证你主动选中的 AI 和网页知识主张。',
    heroLede: '在 ChatGPT、千问、Claude 或任意网页选中一段回答。Spark 检查其中的知识主张是否有适用来源支持，并展示可信状态、具体来源和证据缺口。',
    productKicker: '产品',
    productTitle: '不是新的聊天机器人，而是 AI 答案的可信验证外挂。',
    productBody: 'AI 模型越来越快、越来越会调用工具，但用户仍然需要知道：一个答案是真实世界知识的积累，还是模型自信编造的幻觉。Spark 在模型之外增加 KNE 知识可信层，把 AI 输出视为需要检查的知识主张，而不是直接接受的最终真理。',
    accessKicker: '使用入口',
    accessTitle: '一个 KNE 可信评估层，通过三个聚焦入口使用。',
    accessBody: 'Chrome 与 Edge 使用同一套选中文本验证流程；扣子集成把相同的 Spark API 公开结果接入 Agent 工作流，同时不暴露私有 KNE Core。',
    accessStatusLabel: '状态',
    accessItems: [
      ['Chrome', '已公开', 'Spark 0.3.1 新增中英文主张查询规划，同时保留选中文本、侧边栏和 KNE 公开结果契约。', '安装 Chrome 插件', 'chrome'],
      ['Microsoft Edge', '已公开', 'Spark 0.3.1 与 Chrome 使用相同的双语查询规划、验证流程和 KNE 公开结果契约。', '安装 Edge 插件', 'edge'],
      ['扣子', '已公开', 'Spark 0.3.1 为 Agent 工作流准备同一套双语证据检索输入，同时不暴露私有 KNE Core。', '打开扣子插件', 'coze'],
    ],
    capabilities: [
      ['知识主张识别', '判断 AI 回答中是否包含事实、安全、医疗、法律、技术或时效敏感的知识主张。'],
      ['基于证据的可信状态', '通过 KNE 候选来源、证据规则、适用性网关和可信策略返回 source_supported、source_missing、needs_review、conflicted、outdated、high_risk、not_knowledge 等状态。'],
      ['贡献审核队列', '用户可以提交来源、纠错和质疑。这些贡献进入人工审核，不会直接污染 KNE 核心数据。'],
    ],
    outcomesKicker: '为什么重要',
    outcomesTitle: '当 AI 成为知识入口，可信需要独立基础设施。',
    outcomesBody: 'Agent、Skill 和工具让 AI 拥有手脚。Spark 关注这些行动之前的知识底层：哪些可以信，哪些缺少来源，哪些在影响人类决策前必须复核。',
    outcomes: [
      ['降低幻觉风险', '把 AI 回答转化为可审阅的知识主张，并展示来源与风险信号。'],
      ['保护高风险决策', '对安全、健康、法律等敏感主张优先提示风险和人工复核需求。'],
      ['逐步建设 KNE', '通过审核后的用户纠错和来源提交，形成 Claim-Evidence 知识可信图谱。'],
      ['保持模型独立', 'Spark 设计为服务主流 AI 平台旁侧的可信层，而不是替代它们。'],
    ],
    demoKicker: '使用流程',
    demoTitle: '在日常 AI 使用中完成轻量验证。',
    demoBody: '第一阶段聚焦主动选中文本验证。用户自己选择要验证的 AI 回答或网页内容；Spark 默认不自动抓取页面。输出是谨慎可信状态，不是最终真理认证。',
    openLive: '打开 Chrome Web Store',
    demoPanelKicker: 'AI 输出',
    demoPanelTitle: 'Spark 把模型输出视为待验证主张。',
    entries: [
      ['主张', '野外蘑菇是否安全不能只通过颜色判断。', '识别为安全相关事实主张'],
      ['风险', '食物毒性和中毒处理建议需要谨慎来源。', '高风险安全领域'],
      ['来源提示', '优先使用 CDC、Poison Control 或官方医学/公共卫生来源。', 'KNE 来源策略'],
    ],
    reviewable: '可审阅',
    basis: '依据',
    actions: ['验证', '质疑', '提交来源'],
    pathKicker: 'KNE 来源层',
    pathTitle: '验证与生成分离。',
    path: 'AI 回答 -> 知识主张 -> 来源证据 -> 可信状态 -> 审核队列',
    claims: [
      ['source_supported', '当前主张存在与风险等级匹配的可接受来源支持。'],
      ['source_missing', '当前 KNE 参考语料中没有找到可支持该主张的候选来源。'],
      ['needs_review', '高风险、冲突、过期或来源不足的主张需要人工复核。'],
    ],
    demoSample: '可信状态',
    claimSource: '输出：谨慎验证结果，不是最终真理认证',
    cases: [
      ['日常 AI 用户', '检查 AI 关于食物、安全、健康、金融和技术的回答是否有可靠来源支持。', ['答案验证', '证据缺口', '风险提示']],
      ['研究者和构建者', '把模型输出与来源证据分离，同时收集纠错和来源提交。', ['来源层', '审核队列', '主张历史']],
      ['未来 KNE 运营者', '维护权威来源库、可信策略版本和经过审核的贡献管线。', ['来源注册', '可信策略', '人工审核']],
    ],
    businessKicker: '数字化原生商业模式',
    businessTitle: '通过云端软件、浏览器插件和获准的平台集成交付。',
    business: [
      'Chrome、Edge 插件和获准的平台集成共同使用 Spark 公开验证 API。',
      '早期测试面向已经依赖 AI 工具的用户。Spark 在用户主动验证后，轻量记录 ChatGPT、千问、Claude、Gemini、豆包和普通网页的来源上下文。',
      '计划收入模式包括个人订阅、团队验证席位和 KNE 验证 API 访问。',
    ],
    companyKicker: '公司',
    companyTitle: 'KNE Spark 正在构建 AI 时代独立的知识可信层。',
    companyBody: [
      '当前 Spark 2.0 聚焦一个可验证入口：帮助用户在使用主流 AI 产品时验证知识主张。旧版 Spark Web Chat 已不再作为主产品入口。',
      'KNE 的长期愿景是建立 Claim-Evidence 知识可信系统，保护真实世界知识，抵抗幻觉、污染、弱来源和商业操控。Spark 是这个系统面向用户的第一个入口。',
    ],
    teamKicker: '团队',
    teamTitle: '创始人主导的产品与工程开发。',
    founder: 'Spark 创始人与构建者，负责产品、工程、AI 记忆、KNE 来源系统和知识验证。',
    privacy: '隐私政策',
    support: '支持',
    terms: '使用条款',
    panel: {
      title: 'Spark Verify',
      user1: '颜色鲜艳的蘑菇是不是都有毒？',
      spark: 'Spark 识别到安全风险主张，并检查是否存在来源支持。',
      user2: '可信状态：高风险。需要官方毒理或公共卫生来源。',
      cards: [
        ['主张', '该回答包含可能影响现实安全的知识。'],
        ['来源', '需要官方医学或公共卫生来源支持。'],
        ['复核', '高风险主张不能自动视为已认证。'],
      ],
    },
  },
} as const

export default function HomePage() {
  const { i18n } = useTranslation()
  const locale: Locale = i18n.language === 'en' ? 'en' : 'zh'
  const t = copy[locale]

  return (
    <main className={styles.root}>
      <section className={styles.hero}>
        <nav className={styles.nav} aria-label={t.navLabel}>
          <a className={styles.brand} href="/">
            <span className={styles.brandMark} />
            <span>Spark KNE</span>
          </a>
          <div className={styles.navLinks}>
            <a href="#product">{t.nav[0]}</a>
            <a href="#workflow">{t.nav[1]}</a>
            <a href="#company">{t.nav[2]}</a>
            <a href="#team">{t.nav[3]}</a>
            <button
              className={styles.langToggle}
              type="button"
              onClick={() => persistLocale(locale === 'en' ? 'zh' : 'en')}
            >
              {locale === 'en' ? '中文' : 'EN'}
            </button>
          </div>
        </nav>

        <div className={styles.heroVisual} aria-hidden="true">
          <div className={styles.window}>
            <div className={styles.windowTop}>
              <span />
              <span />
              <span />
              <strong>{t.windowLabel}</strong>
            </div>
            <div className={styles.panelGrid}>
              <div className={styles.chatStream}>
                <div className={styles.userLine}>{t.panel.user1}</div>
                <div className={styles.sparkLine}>{t.panel.spark}</div>
                <div className={styles.userLine}>{t.panel.user2}</div>
              </div>
              <div className={styles.cognitionPanel}>
                <div className={styles.panelTitle}>{t.panel.title}</div>
                {t.panel.cards.map(([label, text]) => (
                  <div className={styles.memoryItem} key={text}>
                    <span>{label}</span>
                    <p>{text}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className={styles.heroContent}>
          <p className={styles.kicker}>{t.heroKicker}</p>
          <h1>{t.heroTitle}</h1>
          <p className={styles.lede}>{t.heroLede}</p>
          <p className={styles.betaNote}>{t.betaNote}</p>
          <div className={styles.heroActions}>
            <a className={styles.primaryAction} href={installUrl(locale, 'hero')} target="_blank" rel="noreferrer">{t.testInvite}</a>
            <a className={styles.secondaryAction} href={EDGE_STORE_URL} target="_blank" rel="noreferrer">{t.edgeInvite}</a>
          </div>
        </div>
      </section>

      <section id="product" className={styles.section}>
        <div className={styles.sectionHead}>
          <p className={styles.kicker}>{t.productKicker}</p>
          <h2>{t.productTitle}</h2>
          <p>{t.productBody}</p>
        </div>
        <div className={styles.capabilityGrid}>
          {t.capabilities.map(([label, text]) => (
            <article className={styles.capability} key={label}>
              <span>{label}</span>
              <p>{text}</p>
            </article>
          ))}
        </div>
      </section>

      <section className={styles.accessSection} aria-labelledby="access-title">
        <div className={styles.sectionHead}>
          <p className={styles.kicker}>{t.accessKicker}</p>
          <h2 id="access-title">{t.accessTitle}</h2>
          <p>{t.accessBody}</p>
        </div>
        <div className={styles.accessGrid}>
          {t.accessItems.map(([name, status, description, action, channel]) => {
            const href = channel === 'chrome'
              ? installUrl(locale, 'workflow')
              : channel === 'edge'
                ? EDGE_STORE_URL
                : COZE_STORE_URL
            const external = true
            return (
              <article className={styles.accessItem} key={name}>
                <div className={styles.accessItemTop}>
                  <h3>{name}</h3>
                  <span>{t.accessStatusLabel}: {status}</span>
                </div>
                <p>{description}</p>
                <a href={href} target={external ? '_blank' : undefined} rel={external ? 'noreferrer' : undefined}>{action}</a>
              </article>
            )
          })}
        </div>
      </section>

      <section className={styles.outcomesSection}>
        <div className={styles.twoColumn}>
          <div>
            <p className={styles.kicker}>{t.outcomesKicker}</p>
            <h2>{t.outcomesTitle}</h2>
          </div>
          <div className={styles.copyBlock}>
            <p>{t.outcomesBody}</p>
          </div>
        </div>
        <div className={styles.outcomeGrid}>
          {t.outcomes.map(([label, text]) => (
            <article className={styles.outcomeCard} key={label}>
              <span>{label}</span>
              <p>{text}</p>
            </article>
          ))}
        </div>
      </section>

      <section id="workflow" className={styles.demoSection}>
        <div className={styles.demoHead}>
          <div>
            <p className={styles.kicker}>{t.demoKicker}</p>
            <h2>{t.demoTitle}</h2>
            <p>{t.demoBody}</p>
          </div>
          <a className={styles.demoLink} href={installUrl(locale, 'workflow')} target="_blank" rel="noreferrer">{t.openLive}</a>
        </div>

        <div className={styles.demoGrid}>
          <div className={styles.understandingDemo}>
            <div className={styles.demoBar}>
              <span>{t.demoBrand}</span>
              <strong>{t.demoVerify}</strong>
            </div>
            <div className={styles.demoIntro}>
              <div>
                <p className={styles.demoKicker}>{t.demoPanelKicker}</p>
                <h3>{t.demoPanelTitle}</h3>
              </div>
              <div className={styles.demoCount}>3</div>
            </div>
            <div className={styles.entryList}>
              {t.entries.map(([type, content, basis]) => (
                <article className={styles.entryCard} key={content}>
                  <div className={styles.entryTop}>
                    <span>{type}</span>
                    <span>{t.reviewable}</span>
                  </div>
                  <p>{content}</p>
                  <small>{t.basis}: {basis}</small>
                  <div className={styles.entryActions}>
                    {t.actions.map((action) => <span key={action}>{action}</span>)}
                  </div>
                </article>
              ))}
            </div>
          </div>

          <div className={styles.pathDemo}>
            <div className={styles.pathHeader}>
              <p className={styles.demoKicker}>{t.pathKicker}</p>
              <h3>{t.pathTitle}</h3>
              <span>{t.path}</span>
            </div>
            <div className={styles.claimList}>
              {t.claims.map(([type, content]) => (
                <article className={styles.claimCard} key={content}>
                  <div className={styles.claimTags}>
                    <span>{type}</span>
                    <span>{t.demoSample}</span>
                  </div>
                  <p>{content}</p>
                  <small>{t.claimSource}</small>
                </article>
              ))}
            </div>
          </div>
        </div>

        <div className={styles.caseGrid}>
          {t.cases.map(([role, scenario, tags]) => (
            <article className={styles.caseCard} key={role}>
              <h3>{role}</h3>
              <p>{scenario}</p>
              <div>
                {tags.map((tag) => <span key={tag}>{tag}</span>)}
              </div>
            </article>
          ))}
        </div>
      </section>

      <section className={styles.band}>
        <div>
          <p className={styles.kicker}>{t.businessKicker}</p>
          <h2>{t.businessTitle}</h2>
        </div>
        <ul className={styles.businessList}>
          {t.business.map((item) => <li key={item}>{item}</li>)}
        </ul>
      </section>

      <section id="company" className={styles.section}>
        <div className={styles.twoColumn}>
          <div>
            <p className={styles.kicker}>{t.companyKicker}</p>
            <h2>{t.companyTitle}</h2>
          </div>
          <div className={styles.copyBlock}>
            {t.companyBody.map((item) => <p key={item}>{item}</p>)}
          </div>
        </div>
      </section>

      <section id="team" className={styles.section}>
        <div className={styles.teamLayout}>
          <div>
            <p className={styles.kicker}>{t.teamKicker}</p>
            <h2>{t.teamTitle}</h2>
          </div>
          <div className={styles.teamCard}>
            <div className={styles.avatar}>SZ</div>
            <div>
              <h3>Seth Zhai</h3>
              <p>{t.founder}</p>
              <a href="mailto:seth@knespark.com">seth@knespark.com</a>
            </div>
          </div>
        </div>
      </section>

      <footer className={styles.footer}>
        <span>© 2026 KNE Spark</span>
        <span className={styles.footerLinks}>
          <a href="/privacy">{t.privacy}</a>
          <a href="/terms">{t.terms}</a>
          <a href="/support">{t.support}</a>
          <a href={CONTACT_URL}>seth@knespark.com</a>
        </span>
      </footer>
    </main>
  )
}
